// src/admin/pages/MemberDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { Card } from "../ui/Card";
import { Badge } from "../ui/Badge";
import { EmptyState } from "../ui/EmptyState";

// ---------------- helpers ----------------
function normalize(s) {
  return String(s || "").toLowerCase().trim();
}

function fullNameFromContact(c) {
  const first = c?.first_name || "";
  const last = c?.last_name || "";
  return `${first} ${last}`.trim() || "Unnamed";
}

function fullNameFromDriver(d) {
  const first = d?.first_name || "";
  const last = d?.last_name || "";
  return `${first} ${last}`.trim() || "Unnamed driver";
}

function overlaps(aStart, aEnd, bStart, bEnd) {
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  const aS = new Date(aStart).getTime();
  const aE = new Date(aEnd).getTime();
  const bS = new Date(bStart).getTime();
  const bE = new Date(bEnd).getTime();
  return aS <= bE && bS <= aE;
}

function daysBetween(startISO, endISO) {
  if (!startISO || !endISO) return null;
  const a = new Date(startISO);
  const b = new Date(endISO);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const ms = b.getTime() - a.getTime();
  const raw = Math.round(ms / (1000 * 60 * 60 * 24));
  return raw === 0 ? 1 : raw;
}

function calcAgeFromDOB(dobISO) {
  if (!dobISO) return null;
  const dob = new Date(dobISO);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
  return age;
}

function riskForMembership(m) {
  if (!m) return { key: "neutral", label: "—" };
  if (!m.age_verified) return { key: "danger", label: "🔴 HIGH (Age not verified)" };
  if (m.status !== "active") return { key: "warning", label: "🟡 MED (Not active)" };
  if ((m.days_remaining ?? 0) <= 2) return { key: "warning", label: "🟡 MED (Low days)" };
  return { key: "success", label: "🟢 LOW" };
}

function toneForBookingStatus(status) {
  const s = String(status || "").toLowerCase();
  if (s === "approved") return "success";
  if (s === "requested") return "warning";
  if (s === "active") return "info";
  if (s === "cancelled") return "neutral";
  if (s === "completed") return "neutral";
  return "neutral";
}

function toneForExpiry(expiryISO) {
  if (!expiryISO) return "neutral";
  const t = new Date(expiryISO).getTime();
  if (Number.isNaN(t)) return "neutral";
  const now = Date.now();
  const days = Math.round((t - now) / (1000 * 60 * 60 * 24));
  if (days < 0) return "danger";
  if (days <= 30) return "warning";
  return "success";
}

function docLabel(docType) {
  const t = String(docType || "");
  if (t === "proof_of_insurance") return "Proof of Insurance";
  if (t === "drivers_license_front") return "Driver’s License (Front)";
  if (t === "drivers_license_back") return "Driver’s License (Back)";
  if (t === "drivers_abstract") return "Driver Abstract";
  if (t === "boating_license") return "Boating License";
  return t || "Document";
}

function buildEditForm(mem, emergRow) {
  const fmt =
    mem?.crm_contacts?.format && typeof mem.crm_contacts.format === "object"
      ? mem.crm_contacts.format
      : {};

  return {
    first_name: mem?.crm_contacts?.first_name || "",
    last_name: mem?.crm_contacts?.last_name || "",
    email: mem?.crm_contacts?.email || "",
    phone: mem?.crm_contacts?.phone || "",
    stage: mem?.crm_contacts?.stage || "prospect",
    source: mem?.crm_contacts?.source || "",
    date_of_birth: mem?.crm_contacts?.date_of_birth || "",

    tier: mem?.tier || "Explore",
    billing: mem?.billing || "None",
    membership_status: mem?.status || "prospect",
    can_book: !!mem?.can_book,
    age_verified: !!mem?.age_verified,
    start_date: mem?.start_date || "",
    renewal_date: mem?.renewal_date || "",
    days_remaining: mem?.days_remaining ?? 0,

    format_family: fmt.family || "",
    format_occupation: fmt.occupation || "",
    format_recreation: fmt.recreation || "",
    format_motivation: fmt.motivation || "",
    format_animals: fmt.animals || "",
    format_teams: fmt.teams || "",

    emergency_name: emergRow?.name || "",
    emergency_relationship: emergRow?.relationship || "",
    emergency_phone: emergRow?.phone || "",
    emergency_email: emergRow?.email || "",
  };
}

// ---------------- component ----------------
export default function MemberDetail() {
  const { memberId } = useParams();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [showResults, setShowResults] = useState(false);

  const [row, setRow] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [emergency, setEmergency] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [documents, setDocuments] = useState([]);

  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [checkIn, setCheckIn] = useState("");

  const [allRows, setAllRows] = useState([]);

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    stage: "prospect",
    source: "",
    date_of_birth: "",

    tier: "Explore",
    billing: "None",
    membership_status: "prospect",
    can_book: false,
    age_verified: false,
    start_date: "",
    renewal_date: "",
    days_remaining: 0,

    format_family: "",
    format_occupation: "",
    format_recreation: "",
    format_motivation: "",
    format_animals: "",
    format_teams: "",

    emergency_name: "",
    emergency_relationship: "",
    emergency_phone: "",
    emergency_email: "",
  });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [saveErr, setSaveErr] = useState("");

  const setEdit = (k, v) => setEditForm((prev) => ({ ...prev, [k]: v }));

  const searchResults = useMemo(() => {
    const q = normalize(query);
    if (!q) return [];
    return allRows
      .filter((r) => {
        const c = r.contact || {};
        const m = r.membership || {};
        const hay = normalize(
          `${c.first_name || ""} ${c.last_name || ""} ${c.email || ""} ${c.phone || ""} ${m.tier || ""} ${m.status || ""}`
        );
        return hay.includes(q);
      })
      .slice(0, 10);
  }, [allRows, query]);

  const membership = row?.membership || null;
  const contact = row?.contact || null;

  const risk = useMemo(() => riskForMembership(membership), [membership]);
  const dob = contact?.date_of_birth || null;
  const age = useMemo(() => calcAgeFromDOB(dob), [dob]);

  const eligibleVehicles = useMemo(() => {
    if (!membership) return [];
    return vehicles.filter(
      (v) => Array.isArray(v.tier_access) && v.tier_access.includes(membership.tier)
    );
  }, [vehicles, membership]);

  const eligibleVehiclesAvailable = useMemo(() => {
    return eligibleVehicles.filter((v) => v.status === "available");
  }, [eligibleVehicles]);

  const selectedVehicle = useMemo(() => {
    return selectedVehicleId ? vehicles.find((v) => v.id === selectedVehicleId) || null : null;
  }, [vehicles, selectedVehicleId]);

  const memberBookingsOnly = useMemo(() => {
    const id = membership?.id;
    if (!id) return [];
    return (bookings || []).filter((b) => b.membership_id === id);
  }, [bookings, membership?.id]);

  function bookingDays(b) {
    const d = daysBetween(b.start_date, b.end_date);
    return d ? Math.max(0, d) : 0;
  }

  const daysBreakdown = useMemo(() => {
    const list = memberBookingsOnly;

    const status = (s) => String(s || "").toLowerCase();
    const isCompleted = (s) => status(s) === "completed";
    const isOpen = (s) => ["requested", "approved", "active"].includes(status(s));

    const usedDays = list
      .filter((b) => isCompleted(b.status))
      .reduce((sum, b) => sum + bookingDays(b), 0);

    const openDays = list
      .filter((b) => isOpen(b.status))
      .reduce((sum, b) => sum + bookingDays(b), 0);

    return { usedDays, openDays };
  }, [memberBookingsOnly]);

  const projectedRemaining = useMemo(() => {
    const remaining = Number(membership?.days_remaining ?? 0);
    return Math.max(0, remaining - (daysBreakdown.openDays || 0));
  }, [membership?.days_remaining, daysBreakdown.openDays]);

  function allBookingsApprovedActive(list) {
    return (list || []).filter((b) =>
      ["approved", "active"].includes(String(b.status || "").toLowerCase())
    );
  }

  function bookingsForVehicle(vehicleId, list) {
    return (list || []).filter((b) => b.vehicle_id === vehicleId);
  }

  const availabilityStatus = useMemo(() => {
    if (!membership) return { ok: null, text: "Member not loaded", detail: "" };
    if (!selectedVehicleId || !checkOut || !checkIn) {
      return { ok: null, text: "Enter dates ↑", detail: "" };
    }

    const v = vehicles.find((x) => x.id === selectedVehicleId);
    if (!v) return { ok: false, text: "Vehicle not found", detail: "" };

    if (v.status !== "available") {
      return { ok: false, text: "Not available", detail: `Ops status: ${v.status}` };
    }

    const conflict = bookingsForVehicle(
      selectedVehicleId,
      allBookingsApprovedActive(bookings)
    ).some((b) => overlaps(checkOut, checkIn, b.start_date, b.end_date));

    return conflict
      ? { ok: false, text: "❌ CONFLICT", detail: "Overlaps an approved/active booking." }
      : { ok: true, text: "✓ AVAILABLE", detail: "No overlap with approved/active bookings." };
  }, [membership, selectedVehicleId, checkOut, checkIn, vehicles, bookings]);

  useEffect(() => {
    let cancelled = false;

    async function signDocUrls(docs) {
      const signed = await Promise.all(
        (docs || []).map(async (d) => {
          const path = d.file_path || d.path || null;
          if (!path) return { ...d, signed_url: null };
          const { data, error } = await supabase.storage
            .from("member-docs")
            .createSignedUrl(path, 60 * 60);
          if (error) return { ...d, signed_url: null, signed_error: error.message };
          return { ...d, signed_url: data?.signedUrl || null };
        })
      );
      return signed;
    }

    async function load() {
      setLoading(true);
      setErr(null);

      try {
        const { data: mem, error: memErr } = await supabase
          .from("memberships")
          .select(
            `
            id,
            created_at,
            status,
            tier,
            billing,
            days_remaining,
            can_book,
            age_verified,
            start_date,
            renewal_date,
            contact_id,
            crm_contacts:contact_id (
              id,
              first_name,
              last_name,
              email,
              phone,
              stage,
              source,
              created_at,
              notes,
              date_of_birth,
              format
            )
          `
          )
          .eq("id", memberId)
          .single();

        if (memErr) throw memErr;

        const contactId = mem?.contact_id;

        const { data: veh, error: vehErr } = await supabase
          .from("vehicles")
          .select("id, name, category, status, location, tier_access")
          .order("created_at", { ascending: false });

        if (vehErr) throw vehErr;

        const { data: bks, error: bErr } = await supabase
          .from("bookings")
          .select(
            "id, membership_id, vehicle_id, start_date, end_date, status, pickup_location, concierge, notes, created_at"
          )
          .eq("membership_id", memberId)
          .order("created_at", { ascending: false });

        if (bErr) throw bErr;

        const { data: globalBks, error: gbErr } = await supabase
          .from("bookings")
          .select("id, vehicle_id, start_date, end_date, status, created_at")
          .in("status", ["approved", "active"])
          .order("created_at", { ascending: false })
          .limit(500);

        if (gbErr) throw gbErr;

        const { data: allMem, error: allErr } = await supabase
          .from("memberships")
          .select(
            `
            id,
            status,
            tier,
            billing,
            crm_contacts:contact_id (
              id,
              first_name,
              last_name,
              email,
              phone
            )
          `
          )
          .order("created_at", { ascending: false })
          .limit(500);

        if (allErr) throw allErr;

        let emergRow = null;
        let driverRows = [];
        let docRows = [];

        if (contactId) {
          const { data: emerg, error: emergErr } = await supabase
            .from("contact_emergency")
            .select("contact_id, name, relationship, phone, email")
            .eq("contact_id", contactId)
            .maybeSingle();

          if (emergErr) throw emergErr;
          emergRow = emerg || null;

          const { data: dr, error: drErr } = await supabase
            .from("contact_drivers")
            .select(
              `
              id,
              contact_id,
              is_primary,
              first_name,
              last_name,
              phone,
              email,
              driver_license_number,
              driver_license_expiry,
              driver_license_province,
              abstract_required,
              boating_license_number,
              boating_license_expiry,
              created_at
            `
            )
            .eq("contact_id", contactId)
            .order("is_primary", { ascending: false })
            .order("created_at", { ascending: true });

          if (!drErr) driverRows = dr || [];

          const { data: docs, error: docsErr } = await supabase
            .from("contact_documents")
            .select(
              `
              id,
              contact_id,
              driver_id,
              doc_type,
              file_path,
              file_name,
              mime_type,
              expires_on,
              created_at
            `
            )
            .eq("contact_id", contactId)
            .order("created_at", { ascending: false });

          if (!docsErr) docRows = docs || [];
        }

        const signedDocs = await signDocUrls(docRows);

        if (!cancelled) {
          setRow({
            membership: {
              id: mem.id,
              status: mem.status,
              tier: mem.tier,
              billing: mem.billing,
              days_remaining: mem.days_remaining,
              can_book: mem.can_book,
              age_verified: mem.age_verified,
              start_date: mem.start_date,
              renewal_date: mem.renewal_date,
              contact_id: mem.contact_id,
            },
            contact: mem.crm_contacts || null,
          });

          setVehicles(veh || []);
          setBookings([...(bks || []), ...(globalBks || [])]);

          setAllRows(
            (allMem || []).map((x) => ({
              membership: { id: x.id, status: x.status, tier: x.tier, billing: x.billing },
              contact: x.crm_contacts || null,
            }))
          );

          setEmergency(emergRow);
          setDrivers(driverRows);
          setDocuments(signedDocs);
          setEditForm(buildEditForm(mem, emergRow));
        }
      } catch (e) {
        if (!cancelled) setErr(e.message || "Failed to load member.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  useEffect(() => {
  if (!contact) return;
  setQuery(fullNameFromContact(contact));
  setShowResults(false);
}, [contact]);

  useEffect(() => {
    const next = eligibleVehiclesAvailable?.[0]?.id || eligibleVehicles?.[0]?.id || "";
    setSelectedVehicleId(next);
    setCheckOut("");
    setCheckIn("");
  }, [memberId, eligibleVehiclesAvailable, eligibleVehicles]);

  const format = contact?.format && typeof contact.format === "object" ? contact.format : null;

  const docsByDriver = useMemo(() => {
    const map = new Map();
    for (const d of documents || []) {
      const key = d.driver_id || "contact";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(d);
    }
    return map;
  }, [documents]);

  async function handleSaveMember() {
    if (!membership || !contact?.id) return;

    setSaving(true);
    setSaveErr("");
    setSaveMsg("");

    try {
      const contactPayload = {
        first_name: editForm.first_name || null,
        last_name: editForm.last_name || null,
        email: editForm.email || null,
        phone: editForm.phone || null,
        stage: editForm.stage || "prospect",
        source: editForm.source || null,
        date_of_birth: editForm.date_of_birth || null,
        format: {
          family: editForm.format_family || null,
          occupation: editForm.format_occupation || null,
          recreation: editForm.format_recreation || null,
          motivation: editForm.format_motivation || null,
          animals: editForm.format_animals || null,
          teams: editForm.format_teams || null,
        },
      };

      const membershipPayload = {
        tier: editForm.tier,
        billing: editForm.billing,
        status: editForm.membership_status,
        can_book: !!editForm.can_book,
        age_verified: !!editForm.age_verified,
        start_date: editForm.start_date || null,
        renewal_date: editForm.renewal_date || null,
        days_remaining: Number(editForm.days_remaining || 0),
      };

      const emergencyHasAny =
        editForm.emergency_name ||
        editForm.emergency_relationship ||
        editForm.emergency_phone ||
        editForm.emergency_email;

      const { error: contactErr } = await supabase
        .from("crm_contacts")
        .update(contactPayload)
        .eq("id", contact.id);

      if (contactErr) throw contactErr;

      const { error: membershipErr } = await supabase
        .from("memberships")
        .update(membershipPayload)
        .eq("id", membership.id);

      if (membershipErr) throw membershipErr;

      if (emergencyHasAny) {
        const { error: emergencyErr } = await supabase
          .from("contact_emergency")
          .upsert(
            {
              contact_id: contact.id,
              name: editForm.emergency_name || null,
              relationship: editForm.emergency_relationship || null,
              phone: editForm.emergency_phone || null,
              email: editForm.emergency_email || null,
            },
            { onConflict: "contact_id" }
          );

        if (emergencyErr) throw emergencyErr;
      }

      setRow((prev) => ({
        ...prev,
        contact: {
          ...(prev?.contact || {}),
          ...contactPayload,
        },
        membership: {
          ...(prev?.membership || {}),
          ...membershipPayload,
        },
      }));

      setEmergency(
        emergencyHasAny
          ? {
              contact_id: contact.id,
              name: editForm.emergency_name || null,
              relationship: editForm.emergency_relationship || null,
              phone: editForm.emergency_phone || null,
              email: editForm.emergency_email || null,
            }
          : null
      );

      setIsEditing(false);
      setSaveMsg("Member details updated.");
    } catch (e) {
      setSaveErr(e.message || "Failed to save member changes.");
    } finally {
      setSaving(false);
    }
  }

  function handleCancelEdit() {
    if (!membership || !row) return;

    const mem = {
      id: row.membership?.id,
      status: row.membership?.status,
      tier: row.membership?.tier,
      billing: row.membership?.billing,
      days_remaining: row.membership?.days_remaining,
      can_book: row.membership?.can_book,
      age_verified: row.membership?.age_verified,
      start_date: row.membership?.start_date,
      renewal_date: row.membership?.renewal_date,
      contact_id: row.membership?.contact_id,
      crm_contacts: row.contact || null,
    };

    setEditForm(buildEditForm(mem, emergency));
    setSaveMsg("");
    setSaveErr("");
    setIsEditing(false);
  }

  if (loading) {
    return (
      <div className="page">
        <div className="page__head">
          <div className="page__title">Member</div>
          <div className="page__sub">Loading from Supabase…</div>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="page">
        <div className="page__head page__head--row">
          <div>
            <div className="page__back">
              <Link className="link" to="/admin/members">← Members</Link>
            </div>
            <h1 className="page__title">Member</h1>
            <div className="page__sub">Could not load this member.</div>
          </div>
        </div>

        <Card title="Customer Quick Lookup" subtitle="Search by name, email, phone">
          <LookupSearch
            query={query}
            setQuery={setQuery}
            showResults={showResults}
            setShowResults={setShowResults}
            results={searchResults}
            currentId={null}
            onPick={(picked) => navigate(`/admin/members/${picked.membership.id}`)}
          />
        </Card>

        <div className="adminAuth__error" style={{ marginTop: 12 }}>{err}</div>
        <EmptyState title="Member not found" hint="Check the URL or pick a member from search." />
      </div>
    );
  }

  if (!membership) {
    return (
      <div className="page">
        <EmptyState title="Member not found" hint="Pick a member from search." />
      </div>
    );
  }

  const name = fullNameFromContact(contact);

  return (
    <div className="page">
      <div className="page__head page__head--row">
        <div>
          <div className="page__back">
            <Link className="link" to="/admin/members">← Members</Link>
          </div>
          <h1 className="page__title">{name}</h1>
          <div className="page__sub">{contact?.email || "—"} • {contact?.phone || "—"}</div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {!isEditing ? (
            <button
              className="btn btn--primary"
              type="button"
              onClick={() => {
                setSaveMsg("");
                setSaveErr("");
                setIsEditing(true);
              }}
            >
              Edit Member
            </button>
          ) : (
            <>
              <button
                className="btn btn--primary"
                type="button"
                onClick={handleSaveMember}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>

              <button
                className="btn btn--ghost"
                type="button"
                onClick={handleCancelEdit}
                disabled={saving}
              >
                Cancel
              </button>
            </>
          )}

          <Link
            className="btn btn--ghost"
            to={`/admin/bookings/new?membershipId=${membership.id}&contactId=${membership.contact_id}`}
          >
            + Create Booking
          </Link>
        </div>
      </div>

      {saveMsg ? <div className="memMuted">{saveMsg}</div> : null}
      {saveErr ? <div className="adminAuth__error" style={{ marginTop: 10 }}>{saveErr}</div> : null}

      <div className="memGrid">
        {/* LEFT */}
        <div className="memCol">
          <Card
            title="Customer Profile"
            subtitle={isEditing ? "Edit mode enabled" : "Quick lookup + member info"}
            right={
              <div className="memTopRight">
                <div className="memRisk">
                  <div className="memLabel">Risk</div>
                  <Badge tone={risk.key}>{risk.label}</Badge>
                </div>
              </div>
            }
          >
            <LookupSearch
              query={query}
              setQuery={setQuery}
              showResults={showResults}
              setShowResults={setShowResults}
              results={searchResults}
              currentId={membership.id}
              onPick={(picked) => navigate(`/admin/members/${picked.membership.id}`)}
            />

            <div className="memLookupGrid" style={{ marginTop: 14 }}>
              <div className="memInfoBlock">
                <div className="memBlockTitle">Customer Info</div>

                {!isEditing ? (
                  <>
                    <div className="memCustomerName">{name}</div>
                    <KV k="Contact ID" v={contact?.id || "—"} />
                    <KV k="Email" v={contact?.email || "—"} />
                    <KV k="Phone" v={contact?.phone || "—"} />
                    <KV k="DOB" v={contact?.date_of_birth || "—"} />
                    <KV k="Age (derived)" v={age ?? "—"} />
                    <KV k="CRM Stage" v={<Badge tone="neutral">{contact?.stage || "—"}</Badge>} />
                    <KV k="Source" v={contact?.source || "—"} />
                  </>
                ) : (
                  <>
                    <div className="formRow2">
                      <div>
                        <div className="memLabel">First name</div>
                        <input
                          className="memInput"
                          value={editForm.first_name}
                          onChange={(e) => setEdit("first_name", e.target.value)}
                        />
                      </div>
                      <div>
                        <div className="memLabel">Last name</div>
                        <input
                          className="memInput"
                          value={editForm.last_name}
                          onChange={(e) => setEdit("last_name", e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="formRow2">
                      <div>
                        <div className="memLabel">Email</div>
                        <input
                          className="memInput"
                          value={editForm.email}
                          onChange={(e) => setEdit("email", e.target.value)}
                        />
                      </div>
                      <div>
                        <div className="memLabel">Phone</div>
                        <input
                          className="memInput"
                          value={editForm.phone}
                          onChange={(e) => setEdit("phone", e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="formRow2">
                      <div>
                        <div className="memLabel">DOB</div>
                        <input
                          className="memInput"
                          type="date"
                          value={editForm.date_of_birth}
                          onChange={(e) => setEdit("date_of_birth", e.target.value)}
                        />
                      </div>
                      <div>
                        <div className="memLabel">Age (derived)</div>
                        <div className="memInput" style={{ display: "flex", alignItems: "center" }}>
                          {calcAgeFromDOB(editForm.date_of_birth) ?? "—"}
                        </div>
                      </div>
                    </div>

                    <div className="formRow2">
                      <div>
                        <div className="memLabel">CRM Stage</div>
                        <select
                          className="memSelect"
                          value={editForm.stage}
                          onChange={(e) => setEdit("stage", e.target.value)}
                        >
                          <option value="prospect">prospect</option>
                          <option value="active">active</option>
                          <option value="paused">paused</option>
                          <option value="cancelled">cancelled</option>
                        </select>
                      </div>
                      <div>
                        <div className="memLabel">Source</div>
                        <input
                          className="memInput"
                          value={editForm.source}
                          onChange={(e) => setEdit("source", e.target.value)}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="memInfoBlock">
                <div className="memBlockTitle">Membership</div>

                {!isEditing ? (
                  <>
                    <KV k="Tier" v={<Badge tone="info">{membership.tier}</Badge>} />
                    <KV k="Billing" v={membership.billing || "—"} />
                    <KV
                      k="Status"
                      v={
                        <Badge
                          tone={
                            membership.status === "active"
                              ? "success"
                              : membership.status === "prospect"
                              ? "warning"
                              : "neutral"
                          }
                        >
                          {membership.status}
                        </Badge>
                      }
                    />
                    <KV
                      k="Can book"
                      v={
                        <Badge tone={membership.can_book ? "success" : "warning"}>
                          {membership.can_book ? "Yes" : "No"}
                        </Badge>
                      }
                    />
                    <KV
                      k="Age verified"
                      v={
                        <Badge tone={membership.age_verified ? "success" : "danger"}>
                          {membership.age_verified ? "Yes" : "No"}
                        </Badge>
                      }
                    />
                    <KV k="Start" v={membership.start_date || "—"} />
                    <KV k="Renewal" v={membership.renewal_date || "—"} />
                  </>
                ) : (
                  <>
                    <div className="formRow2">
                      <div>
                        <div className="memLabel">Tier</div>
                        <select
                          className="memSelect"
                          value={editForm.tier}
                          onChange={(e) => setEdit("tier", e.target.value)}
                        >
                          <option value="Explore">Explore</option>
                          <option value="Adventure">Adventure</option>
                          <option value="Elite">Elite</option>
                          <option value="Platinum">Platinum</option>
                        </select>
                      </div>
                      <div>
                        <div className="memLabel">Billing</div>
                        <select
                          className="memSelect"
                          value={editForm.billing}
                          onChange={(e) => setEdit("billing", e.target.value)}
                        >
                          <option value="None">None</option>
                          <option value="Monthly">Monthly</option>
                          <option value="Annual">Annual</option>
                        </select>
                      </div>
                    </div>

                    <div className="formRow2">
                      <div>
                        <div className="memLabel">Status</div>
                        <select
                          className="memSelect"
                          value={editForm.membership_status}
                          onChange={(e) => setEdit("membership_status", e.target.value)}
                        >
                          <option value="prospect">prospect</option>
                          <option value="active">active</option>
                          <option value="paused">paused</option>
                          <option value="cancelled">cancelled</option>
                        </select>
                      </div>
                      <div>
                        <div className="memLabel">Days Remaining</div>
                        <input
                          className="memInput"
                          type="number"
                          value={editForm.days_remaining}
                          onChange={(e) => setEdit("days_remaining", e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="formRow2">
                      <div>
                        <div className="memLabel">Start</div>
                        <input
                          className="memInput"
                          type="date"
                          value={editForm.start_date}
                          onChange={(e) => setEdit("start_date", e.target.value)}
                        />
                      </div>
                      <div>
                        <div className="memLabel">Renewal</div>
                        <input
                          className="memInput"
                          type="date"
                          value={editForm.renewal_date}
                          onChange={(e) => setEdit("renewal_date", e.target.value)}
                        />
                      </div>
                    </div>

                    <label className="checkRow">
                      <input
                        type="checkbox"
                        checked={!!editForm.can_book}
                        onChange={(e) => setEdit("can_book", e.target.checked)}
                      />
                      <span>Can book</span>
                    </label>

                    <label className="checkRow">
                      <input
                        type="checkbox"
                        checked={!!editForm.age_verified}
                        onChange={(e) => setEdit("age_verified", e.target.checked)}
                      />
                      <span>Age verified</span>
                    </label>
                  </>
                )}
              </div>

              <div className="memInfoBlock">
                <div className="memBlockTitle">Days</div>
                <KV k="Days Remaining" v={membership.days_remaining ?? "—"} />
                <KV k="Open Bookings (not yet deducted)" v={daysBreakdown.openDays} />
                <KV k="Days Used (completed)" v={daysBreakdown.usedDays} />
                <div className="memMuted">
                  Projected remaining after open bookings: <b>{projectedRemaining}</b>
                </div>
              </div>

              <div className="memInfoBlock">
                <div className="memBlockTitle">F.O.R.M.A.T</div>

                {!isEditing ? (
                  format ? (
                    <>
                      <KV k="Family" v={format.family || "—"} />
                      <KV k="Occupation" v={format.occupation || "—"} />
                      <KV k="Recreation" v={format.recreation || "—"} />
                      <KV k="Motivation" v={format.motivation || "—"} />
                      <KV k="Animals" v={format.animals || "—"} />
                      <KV k="Teams" v={format.teams || "—"} />
                    </>
                  ) : (
                    <div className="muted">No FORMAT info yet.</div>
                  )
                ) : (
                  <>
                    <div>
                      <div className="memLabel">Family</div>
                      <input
                        className="memInput"
                        value={editForm.format_family}
                        onChange={(e) => setEdit("format_family", e.target.value)}
                      />
                    </div>
                    <div>
                      <div className="memLabel">Occupation</div>
                      <input
                        className="memInput"
                        value={editForm.format_occupation}
                        onChange={(e) => setEdit("format_occupation", e.target.value)}
                      />
                    </div>
                    <div>
                      <div className="memLabel">Recreation</div>
                      <input
                        className="memInput"
                        value={editForm.format_recreation}
                        onChange={(e) => setEdit("format_recreation", e.target.value)}
                      />
                    </div>
                    <div>
                      <div className="memLabel">Motivation</div>
                      <input
                        className="memInput"
                        value={editForm.format_motivation}
                        onChange={(e) => setEdit("format_motivation", e.target.value)}
                      />
                    </div>
                    <div className="formRow2">
                      <div>
                        <div className="memLabel">Animals</div>
                        <input
                          className="memInput"
                          value={editForm.format_animals}
                          onChange={(e) => setEdit("format_animals", e.target.value)}
                        />
                      </div>
                      <div>
                        <div className="memLabel">Teams</div>
                        <input
                          className="memInput"
                          value={editForm.format_teams}
                          onChange={(e) => setEdit("format_teams", e.target.value)}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="memInfoBlock">
                <div className="memBlockTitle">Emergency Contact</div>

                {!isEditing ? (
                  emergency ? (
                    <>
                      <KV k="Name" v={emergency.name || "—"} />
                      <KV k="Relationship" v={emergency.relationship || "—"} />
                      <KV k="Phone" v={emergency.phone || "—"} />
                      <KV k="Email" v={emergency.email || "—"} />
                    </>
                  ) : (
                    <div className="muted">No emergency contact saved.</div>
                  )
                ) : (
                  <>
                    <div>
                      <div className="memLabel">Name</div>
                      <input
                        className="memInput"
                        value={editForm.emergency_name}
                        onChange={(e) => setEdit("emergency_name", e.target.value)}
                      />
                    </div>
                    <div>
                      <div className="memLabel">Relationship</div>
                      <input
                        className="memInput"
                        value={editForm.emergency_relationship}
                        onChange={(e) => setEdit("emergency_relationship", e.target.value)}
                      />
                    </div>
                    <div className="formRow2">
                      <div>
                        <div className="memLabel">Phone</div>
                        <input
                          className="memInput"
                          value={editForm.emergency_phone}
                          onChange={(e) => setEdit("emergency_phone", e.target.value)}
                        />
                      </div>
                      <div>
                        <div className="memLabel">Email</div>
                        <input
                          className="memInput"
                          value={editForm.emergency_email}
                          onChange={(e) => setEdit("emergency_email", e.target.value)}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="memInfoBlock">
                <div className="memBlockTitle">Notes</div>
                {Array.isArray(contact?.notes) && contact.notes.length ? (
                  <div className="memNotes">
                    {contact.notes.map((n, idx) => (
                      <div key={idx} className="memNote">
                        <div className="memNote__at">{n.at || "—"}</div>
                        <div className="memNote__text">{n.text || ""}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="muted">No notes yet.</div>
                )}
              </div>
            </div>
          </Card>

          <Card title="Drivers & Licenses" subtitle="Primary + additional drivers">
            <div className="stack">
              {drivers.length ? (
                drivers.map((d) => {
                  const isPrimary = !!d.is_primary;
                  const dlTone = toneForExpiry(d.driver_license_expiry);
                  const boatTone = toneForExpiry(d.boating_license_expiry);

                  return (
                    <div key={d.id} className="memInfoBlock">
                      <div
                        className="memBlockTitle"
                        style={{ display: "flex", justifyContent: "space-between", gap: 10 }}
                      >
                        <span>{isPrimary ? "Primary Driver" : "Additional Driver"}</span>
                        {isPrimary ? <Badge tone="info">Primary</Badge> : <Badge tone="neutral">Driver</Badge>}
                      </div>

                      <KV k="Name" v={fullNameFromDriver(d)} />
                      <KV k="Phone" v={d.phone || "—"} />
                      <KV k="Email" v={d.email || "—"} />

                      <div style={{ height: 8 }} />

                      <KV k="License #" v={d.driver_license_number || "—"} />
                      <KV k="Province" v={d.driver_license_province || "—"} />
                      <KV
                        k="License Expiry"
                        v={
                          d.driver_license_expiry ? (
                            <Badge tone={dlTone}>{d.driver_license_expiry}</Badge>
                          ) : (
                            "—"
                          )
                        }
                      />
                      <KV
                        k="Abstract"
                        v={
                          <Badge tone={d.abstract_required ? "warning" : "success"}>
                            {d.abstract_required ? "Required" : "Not required"}
                          </Badge>
                        }
                      />

                      <div style={{ height: 8 }} />

                      <KV k="Boating License #" v={d.boating_license_number || "—"} />
                      <KV
                        k="Boating Expiry"
                        v={
                          d.boating_license_expiry ? (
                            <Badge tone={boatTone}>{d.boating_license_expiry}</Badge>
                          ) : (
                            "—"
                          )
                        }
                      />

                      <div style={{ marginTop: 10 }}>
                        <div className="memLabel">Documents</div>
                        <DocList docs={docsByDriver.get(d.id) || []} />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="muted">No drivers saved yet.</div>
              )}
            </div>
          </Card>

          <Card title="Bookings" subtitle="History & upcoming">
            <div className="memBookings">
              {memberBookingsOnly.length ? (
                memberBookingsOnly.map((b) => {
                  const v = vehicles.find((x) => x.id === b.vehicle_id) || null;
                  return (
                    <div key={b.id} className="memBookingRow">
                      <div className="memBookingRow__top">
                        <div className="memBookingRow__id">{b.id}</div>
                        <Badge tone={toneForBookingStatus(b.status)}>{b.status}</Badge>
                      </div>
                      <div className="memBookingRow__sub">
                        {b.start_date} → {b.end_date} •{" "}
                        {v ? (
                          <Link className="link" to={`/admin/vehicles/${v.id}`}>
                            {v.name}
                          </Link>
                        ) : (
                          b.vehicle_id
                        )}{" "}
                        • Concierge: <span className="muted">{b.concierge || "—"}</span>
                      </div>
                      {b.notes && <div className="muted" style={{ marginTop: 6 }}>{b.notes}</div>}
                    </div>
                  );
                })
              ) : (
                <EmptyState title="No bookings yet" hint="This will populate as bookings are created." />
              )}
            </div>
          </Card>
        </div>

        {/* RIGHT */}
        <div className="memCol">
          <Card title="Availability / Date Check" subtitle="Pick an eligible vehicle + dates">
            <div className="memFormRow">
              <div>
                <div className="memLabel">Vehicle (eligible for this tier)</div>
                <select
                  className="memSelect"
                  value={selectedVehicleId}
                  onChange={(e) => setSelectedVehicleId(e.target.value)}
                >
                  {(eligibleVehiclesAvailable.length ? eligibleVehiclesAvailable : eligibleVehicles).map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} — {v.category} ({v.status})
                    </option>
                  ))}
                  {!eligibleVehicles.length && <option value="">No vehicles match this tier</option>}
                </select>

                {selectedVehicle && (
                  <div className="memVehicleMeta">
                    <Badge tone="info">{selectedVehicle.category}</Badge>
                    <span className="muted">{selectedVehicle.location || "—"}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="memFormRow memFormRow--2">
              <div>
                <div className="memLabel">Check-Out</div>
                <input
                  className="memInput"
                  type="date"
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                />
              </div>
              <div>
                <div className="memLabel">Check-In</div>
                <input
                  className="memInput"
                  type="date"
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                />
              </div>
            </div>

            <div className="memStatusRow">
              <div className="memStatus">
                <div className="memLabel">Days</div>
                <div className="memBig">
                  {checkOut && checkIn ? (daysBetween(checkOut, checkIn) ?? "—") : "—"}
                </div>
              </div>

              <div className="memStatus">
                <div className="memLabel">Availability</div>
                <div>
                  {availabilityStatus.ok === null ? (
                    <Badge tone="neutral">{availabilityStatus.text}</Badge>
                  ) : availabilityStatus.ok ? (
                    <Badge tone="success">{availabilityStatus.text}</Badge>
                  ) : (
                    <Badge tone="danger">{availabilityStatus.text}</Badge>
                  )}
                  {availabilityStatus.detail && <div className="memMuted">{availabilityStatus.detail}</div>}
                </div>
              </div>
            </div>

            <div className="memMuted">
              Checks overlap against <b>approved/active</b> bookings. Ops status must be <b>available</b> in v1.
            </div>
          </Card>

          <Card title="Member Documents" subtitle="Contact-level documents (insurance, etc.)">
            <div className="stack">
              <DocList docs={docsByDriver.get("contact") || []} />
              {!((docsByDriver.get("contact") || []).length) && (
                <div className="muted">No contact documents saved.</div>
              )}
              {documents.some((d) => d.signed_error) && (
                <div className="memMuted">
                  Some download links couldn’t be generated (storage policy). The rows still loaded.
                </div>
              )}
            </div>
          </Card>

          <Card title="Eligible vehicles" subtitle="Quick list for this member tier">
            <div className="memVehicleList">
              {eligibleVehicles.length ? (
                eligibleVehicles.map((v) => (
                  <div key={v.id} className="memVehicleRow">
                    <div className="memVehicleRow__main">
                      <div className="memVehicleRow__name">
                        <Link className="link" to={`/admin/vehicles/${v.id}`}>
                          {v.name}
                        </Link>
                      </div>
                      <div className="memVehicleRow__sub">
                        {v.category} • {v.location || "—"}
                      </div>
                    </div>
                    <Badge
                      tone={
                        v.status === "available"
                          ? "success"
                          : v.status === "maintenance"
                          ? "danger"
                          : "warning"
                      }
                    >
                      {v.status}
                    </Badge>
                  </div>
                ))
              ) : (
                <div className="muted">No eligible vehicles for this tier.</div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ---------- small components ----------
function LookupSearch({ query, setQuery, showResults, setShowResults, results, currentId, onPick }) {
  return (
    <div style={{ position: "relative" }}>
      <div className="memLabel">Quick Lookup</div>
      <input
        className="memInput"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setShowResults(true);
        }}
        onFocus={() => setShowResults(true)}
        placeholder="Search by name, email, phone..."
      />

      {showResults && !!normalize(query) && (
        <div className="memTypeahead">
          {results.length ? (
            results.map((row) => {
              const name = fullNameFromContact(row.contact);
              return (
                <button
                  key={row.membership.id}
                  type="button"
                  className={`memTypeahead__item ${currentId === row.membership.id ? "isActive" : ""}`}
                  onClick={() => {
                    setShowResults(false);
                    setQuery(name);
                    onPick(row);
                  }}
                >
                  <div className="memTypeahead__main">
                    <div className="memTypeahead__name">
                      {name} <span className="memTypeahead__meta">• {row.membership.id.slice(0, 8)}</span>
                    </div>
                    <div className="memTypeahead__sub">
                      {row.contact?.email || "—"} • {row.contact?.phone || "—"}
                    </div>
                  </div>
                  <div className="memTypeahead__right">
                    <Badge tone="info">{row.membership.tier}</Badge>
                    <Badge tone={row.membership.status === "active" ? "success" : "warning"}>
                      {row.membership.status}
                    </Badge>
                  </div>
                </button>
              );
            })
          ) : (
            <div className="memTypeahead__empty">No matches.</div>
          )}
          <div className="memTypeahead__hint">Tip: search “@gmail” or a phone area code.</div>
        </div>
      )}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="memKV">
      <span>{k}</span>
      <div className="memKV__v">{v}</div>
    </div>
  );
}

function DocList({ docs }) {
  if (!docs || !docs.length) return <div className="muted">No documents.</div>;

  return (
    <div className="memNotes">
      {docs.map((d) => {
        const label = docLabel(d.doc_type);
        const expires = d.expires_on || null;
        const expiresTone = toneForExpiry(expires);
        const title = d.file_name || d.file_path || "Document";

        return (
          <div key={d.id} className="memNote">
            <div
              className="memNote__at"
              style={{ display: "flex", justifyContent: "space-between", gap: 10 }}
            >
              <span>{label}</span>
              {expires ? (
                <Badge tone={expiresTone}>Expires: {expires}</Badge>
              ) : (
                <Badge tone="neutral">No expiry</Badge>
              )}
            </div>

            <div
              className="memNote__text"
              style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}
            >
              <span className="muted" title={title}>{title}</span>

              {d.signed_url ? (
                <a className="link" href={d.signed_url} target="_blank" rel="noreferrer">
                  Download
                </a>
              ) : (
                <span className="muted">No link</span>
              )}
            </div>

            {d.signed_error ? <div className="memMuted">Link error: {d.signed_error}</div> : null}
          </div>
        );
      })}
    </div>
  );
}